import { QueryClient } from "@tanstack/react-query";
import React, {
  Dispatch,
  useState,
  useMemo,
  createContext,
  SetStateAction,
  useEffect,
  useContext,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useGetContext } from "server/kas/hooks/useGetContext";
import { useGetMe } from "server/kas/hooks/useGetMe";
import { useGetOrg } from "server/kas/hooks/useGetOrg";
import { useGetStarfleetToken } from "server/kas/hooks/useGetStarfleetToken";
import {
  CONSOLE_2_LOGIN_URL,
  isKasAuthFlow,
  KAS_TOKEN_STORAGE_KEY,
  redirectUserToLogin,
} from "server/kas/utils";

type KasContextType = {
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  user: Record<string, string>;
  setUser: Dispatch<SetStateAction<Record<string, string>>>;
  isUserLoggedIn: boolean;
  setIsUserLoggedIn: Dispatch<SetStateAction<boolean>>;
  isLoggingOut: boolean;
  setIsLoggingOut: Dispatch<SetStateAction<boolean>>;
  currentOrgNgcName: string;
  setCurrentOrgNgcName: Dispatch<SetStateAction<string>>;
  currentNcaId: string;
  setCurrentNcaId: Dispatch<SetStateAction<string>>;
  starfleetIdToken: string;
  setStarfleetIdToken: Dispatch<SetStateAction<string>>;
};
const defaultValues: KasContextType = {
  isLoading: false,
  setIsLoading: () => {},
  user: {},
  setUser: () => {},
  isUserLoggedIn: false,
  setIsUserLoggedIn: () => {},
  isLoggingOut: false,
  setIsLoggingOut: () => {},
  currentOrgNgcName: "",
  setCurrentOrgNgcName: () => {},
  currentNcaId: "",
  setCurrentNcaId: () => {},
  starfleetIdToken: "",
  setStarfleetIdToken: () => {},
};

export const KasContext = createContext<KasContextType | undefined>(undefined);

const KasContextProvider = ({ children }: React.PropsWithChildren) => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(isKasAuthFlow);
  const [user, setUser] = useState({});
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [currentOrgNgcName, setCurrentOrgNgcName] = useState("");
  const [currentNcaId, setCurrentNcaId] = useState("");
  const [starfleetIdToken, setStarfleetIdToken] = useState("");
  const { data: userData, error } = useGetContext({ isKasAuthFlow });
  const { data: userMeData } = useGetMe({ isKasAuthFlow });
  const { data: orgData } = useGetOrg({
    org: currentOrgNgcName,
    isKasAuthFlow,
  });
  const { data: starfleetTokenData } = useGetStarfleetToken({ isKasAuthFlow });

  // console.log("kasContext");
  // console.log("isLoading", isLoading);
  // console.log("isUserLoggedIn", isUserLoggedIn);
  // console.log("isLoggingOut", isLoggingOut);
  // console.log("currentOrgNgcName", currentOrgNgcName);
  // console.log("currentNcaId", currentNcaId);
  // console.log("starfleetIdToken", starfleetIdToken);
  // console.log("userData", userData);
  // console.log("userMeData", userMeData);
  // console.log("user", user);

  // console.log("orgData", orgData);
  // console.log("starfleetTokenData", starfleetTokenData);

  // console.log("ContextError", error);

  // ensure the user's logged in state
  // is updated based off the heartbeat
  useEffect(() => {
    if (userData) {
      setUser({ ...userData });

      // todo - can remove once KAS auto-select org fix is in prod
      userData.orgName && setCurrentOrgNgcName(userData.orgName);
    } else if (error && isKasAuthFlow) {
      // show error here
      // console.log("clear1");
      localStorage.removeItem(KAS_TOKEN_STORAGE_KEY);
      history.push("/login");
    } else {
      setIsUserLoggedIn(false);
    }
  }, [userData]);

  // todo - can remove once KAS auto-select org fix is in prod
  useEffect(() => {
    if (userMeData && !currentOrgNgcName) {
      setCurrentOrgNgcName(userMeData?.user?.roles?.[0]?.org?.name);
    }
  }, [userMeData]);

  // TODO - do this later, we don't care about NCA right now
  // get the user's ngc org (which was created during first login flow as NCA was created)
  // we will use the ngc org to get the org's corresponding NCA ID
  /*
  useEffect(() => {
    if (userMeData) {
      // give priority to orgs the user is an ORG_OWNER of since:
      // (1) if the user was "net new" on brev, this org will be the one that was auto-generated
      //     for them during their first login
      // (2) if the user is already an ORG_OWNER of an org in NGC, the user is guaranteed to be an NCA admin
      //     so they can invite other users to this NCA - (note: these users would have skipped the NCA creation page during their first login to brev)
      const userOrg = userMeData?.userRoles?.find((role) =>
        role.orgRoles.includes("ORG_OWNER")
      );

      setUser({ ...user, id: userMeData.user.starfleetId }); // todo - only needed for local hax, set to userData later

      console.log("userOrg", userOrg);
      if (userOrg) {
        console.log("userOrg.org.name", userOrg.org.name);
        setCurrentOrgNgcName(userOrg.org.name);
      } else {
        console.log(
          "userMeData?.userRoles?.[0]?.org?.name",
          userMeData?.userRoles?.[0]?.org?.name
        );
        setCurrentOrgNgcName(userMeData?.userRoles?.[0]?.org?.name);
      }
    }
  }, [userMeData]);
  */

  // TODO - do this later, we don't care about NCA right now
  // once the user's NGC org is chosen, we need to get the org's NCA ID
  /*
  useEffect(() => {
    if (orgData?.organizations?.billingAccountId) {
      setCurrentNcaId(orgData?.organizations?.billingAccountId);
    }
  }, [orgData]); */

  // set the starfleet token in state + local storage
  useEffect(() => {
    if (starfleetTokenData) {
      localStorage.setItem(KAS_TOKEN_STORAGE_KEY, starfleetTokenData?.token);
      setIsUserLoggedIn(true);
      setStarfleetIdToken(starfleetTokenData?.token);
    }
  }, [starfleetTokenData]);

  // once the starfleet token and NCA ID are set,
  // the KAS portion of login is done (skip NCA part for now)
  useEffect(() => {
    if (starfleetIdToken && currentOrgNgcName) {
      // console.log("loadingDone");
      setIsLoading(false);
    }
  }, [starfleetIdToken, currentOrgNgcName]);

  // when the user isn't logged in anymore or is logging out, reset state
  useEffect(() => {
    if (isKasAuthFlow && ((!isUserLoggedIn && !isLoading) || isLoggingOut)) {
      // console.log("clear2");
      setCurrentOrgNgcName("");
      setCurrentNcaId("");
      setStarfleetIdToken("");
      localStorage.removeItem(KAS_TOKEN_STORAGE_KEY);
      setIsLoggingOut(false);
      setUser({});
      redirectUserToLogin();
    }
  }, [isKasAuthFlow, isUserLoggedIn, isLoading, isLoggingOut]);

  const values = useMemo(
    () => ({
      isLoading,
      setIsLoading,
      user,
      setUser,
      isUserLoggedIn,
      setIsUserLoggedIn,
      isLoggingOut,
      setIsLoggingOut,
      currentOrgNgcName,
      setCurrentOrgNgcName,
      currentNcaId,
      setCurrentNcaId,
      starfleetIdToken,
      setStarfleetIdToken,
    }),
    [
      isLoading,
      user,
      isUserLoggedIn,
      currentOrgNgcName,
      currentNcaId,
      starfleetIdToken,
    ]
  );

  return <KasContext.Provider value={values}>{children}</KasContext.Provider>;
};

// custom hook to read values from
export const useKas = () => {
  const context = useContext(KasContext);
  // throw an error if the user tries to make use of this hook
  // in a component that is not wrapped in the provider
  if (context === undefined && isKasAuthFlow) {
    throw new Error(
      "The `useKasContext` hook must be used within a `KasContextProvider` component"
    );
  }
  return context || defaultValues;
};

export default KasContextProvider;
